import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import { Notification } from '../../models/Notification';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { ApiUrlService } from '../api/api-url.service';
import { LoggerService } from '../logger/logger.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly logger = inject(LoggerService);
  private get notificationsApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/notifications`); }

  private readonly _userNotificationCount = signal<number | null>(null);
  readonly userNotificationCount: Signal<number | null> = this._userNotificationCount.asReadonly();
  readonly userNotificationCount$ = toObservable(this._userNotificationCount);

  private readonly _charityNotificationCount = signal<number | null>(null);
  readonly charityNotificationCount: Signal<number | null> = this._charityNotificationCount.asReadonly();
  readonly charityNotificationCount$ = toObservable(this._charityNotificationCount);

  private readonly _businessNotificationCount = signal<number | null>(null);
  readonly businessNotificationCount: Signal<number | null> = this._businessNotificationCount.asReadonly();
  readonly businessNotificationCount$ = toObservable(this._businessNotificationCount);

  constructor(private httpClient: HttpClientWrapperService) {}

  getNotificationsByUserId(userId: number): Observable<Notification[]> {
    return this.httpClient.get<Notification[]>(`${this.notificationsApiUrl}?userId=${userId}`);
  }

  getNotificationsByCharityId(charityId: number): Observable<Notification[]> {
    return this.httpClient.get<Notification[]>(`${this.notificationsApiUrl}?charityId=${charityId}`);
  }

  loadUnreadCountByUserId(userId: number): void {
    this.getUnreadCountByUserId(userId).subscribe({
      next: (count: number) => this._userNotificationCount.set(count),
      error: (err) => this.logger.error('Failed to fetch user notification count', err)
    });
  }

  getUnreadCountByUserId(userId: number): Observable<number> {
    return this.httpClient.get<{ count: number }>(`${this.notificationsApiUrl}/unread-count?userId=${userId}`)
      .pipe(map(response => response.count));
  }

  loadUnreadCountByCharityId(charityId: number): void {
    this.getUnreadCountByCharityId(charityId).subscribe({
      next: (count: number) => this._charityNotificationCount.set(count),
      error: (err) => this.logger.error('Failed to fetch charity notification count', err)
    });
  }

  getUnreadCountByCharityId(charityId: number): Observable<number> {
    return this.httpClient.get<{ count: number }>(`${this.notificationsApiUrl}/unread-count?charityId=${charityId}`)
      .pipe(map(response => response.count));
  }

  setNotificationsToReadByUserId(userId: number): Observable<Notification[]> {
    return this.httpClient.post<Notification[], number>(`${this.notificationsApiUrl}/read-all?userId=${userId}`, userId);
  }

  setNotificationsToReadByCharityId(charityId: number): Observable<Notification[]> {
    return this.httpClient.post<Notification[], number>(`${this.notificationsApiUrl}/read-all?charityId=${charityId}`, charityId);
  }

  getNotificationsByBusinessId(businessId: number): Observable<Notification[]> {
    return this.httpClient.get<Notification[]>(`${this.notificationsApiUrl}?businessId=${businessId}`);
  }

  loadUnreadCountByBusinessId(businessId: number): void {
    this.getUnreadCountByBusinessId(businessId).subscribe({
      next: (count: number) => this._businessNotificationCount.set(count),
      error: (err) => this.logger.error('Failed to fetch business notification count', err)
    });
  }

  getUnreadCountByBusinessId(businessId: number): Observable<number> {
    return this.httpClient.get<{ count: number }>(`${this.notificationsApiUrl}/unread-count?businessId=${businessId}`)
      .pipe(map(response => response.count));
  }

  setNotificationsToReadByBusinessId(businessId: number): Observable<Notification[]> {
    return this.httpClient.post<Notification[], number>(`${this.notificationsApiUrl}/read-all?businessId=${businessId}`, businessId);
  }
}
